import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import {
  getCancelamentoTipoObra,
  getDetalheEscopoMacro,
  getEquipamentosSfi,
  getEscoposPadraoDoTipo,
  getNomeComposto103,
  getObrasDestinoTransferencia,
  getObrasDestinoTransferenciaTipo,
  getOpcoesNovoTipoObra,
  getOrigensEscopoMacro,
  getPlanejamentoControle,
  getRevisaoTransferenciaTipo,
} from "@/modules/financeiro-obras";
import type {
  DadosCancelamentoTipoObra,
  DetalheEscopoMacro,
  EquipamentosSfi,
  EscopoPadraoDoTipo,
  NomeComposto103,
  ObrasDestinoTipo,
  ObrasDestinoTransferencia,
  OpcoesNovoTipoObra,
  OrigemEscopoMacro,
  ParametrosNomeComposto,
  ParametrosRevisaoTransferenciaTipo,
  PlanejamentoControle,
  RevisaoTransferenciaTipo,
} from "@/modules/financeiro-obras";

export const PLANEJAMENTO_ERROR_MESSAGE =
  "Não foi possível carregar o planejamento da obra. Tente novamente.";
export const PLANEJAMENTO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar o planejamento da obra.";
export const DETALHE_ESCOPO_ERROR_MESSAGE =
  "Não foi possível carregar o escopo macro. Tente novamente.";
export const DETALHE_ESCOPO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar o escopo macro.";

/** Planejamento e Controle da obra (HU 2709): consulta única com cancelamento, recarga e 403. */
export function usePlanejamentoControleDados(obraId: string, enabled = true) {
  return useAsyncQuery<PlanejamentoControle | null>({
    fetcher: (signal) => getPlanejamentoControle(obraId, signal),
    queryKey: `planejamento/${obraId}`,
    initialData: null,
    enabled,
    forbiddenMessage: PLANEJAMENTO_FORBIDDEN_MESSAGE,
    errorMessage: PLANEJAMENTO_ERROR_MESSAGE,
  });
}

/** Detalhe somente leitura de um Escopo Macro (HU 2709). */
export function useDetalheEscopoMacroDados(obraId: string, escopoId: string, enabled = true) {
  return useAsyncQuery<DetalheEscopoMacro | null>({
    fetcher: (signal) => getDetalheEscopoMacro(obraId, escopoId, signal),
    queryKey: `escopo-macro/${obraId}/${escopoId}`,
    initialData: null,
    enabled,
    forbiddenMessage: DETALHE_ESCOPO_FORBIDDEN_MESSAGE,
    errorMessage: DETALHE_ESCOPO_ERROR_MESSAGE,
  });
}

export const NOVO_TIPO_ERROR_MESSAGE =
  "Não foi possível carregar as opções de Tipo de Obra. Tente novamente.";
export const NOVO_TIPO_FORBIDDEN_MESSAGE = "Você não tem permissão para adicionar Tipo de Obra.";

export function useOpcoesNovoTipoObraDados(obraId: string, enabled = true) {
  return useAsyncQuery<OpcoesNovoTipoObra | null>({
    fetcher: (signal) => getOpcoesNovoTipoObra(obraId, signal),
    queryKey: `novo-tipo-obra/${obraId}`,
    initialData: null,
    enabled,
    forbiddenMessage: NOVO_TIPO_FORBIDDEN_MESSAGE,
    errorMessage: NOVO_TIPO_ERROR_MESSAGE,
  });
}

export const CANCELAMENTO_TIPO_ERROR_MESSAGE =
  "Não foi possível carregar os dados do cancelamento. Tente novamente.";
export const CANCELAMENTO_TIPO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para cancelar Tipo de Obra.";

/** Escopos abrangidos e sucessão do dominante do modal Cancelar Tipo de Obra (HU 2717); sempre fresco, sem cache. */
export function useCancelamentoTipoObraDados(obraId: string, obraTipoObraId: string) {
  return useAsyncQuery<DadosCancelamentoTipoObra | null>({
    fetcher: (signal) => getCancelamentoTipoObra(obraId, obraTipoObraId, signal),
    queryKey: `cancelamento-tipo-obra/${obraId}/${obraTipoObraId}`,
    initialData: null,
    forbiddenMessage: CANCELAMENTO_TIPO_FORBIDDEN_MESSAGE,
    errorMessage: CANCELAMENTO_TIPO_ERROR_MESSAGE,
  });
}

export const TRANSFERENCIA_ERROR_MESSAGE =
  "Não foi possível carregar as Obras de destino. Tente novamente.";
export const TRANSFERENCIA_FORBIDDEN_MESSAGE =
  "Você não tem permissão para transferir Escopo Macro.";

/** Obras da mesma embarcação do modal Transferir Escopo (HU 2713); sempre fresco, sem cache. O 400 do servidor (escopo sem Tipo vigente) vira a mensagem do erro. */
export function useObrasDestinoTransferenciaDados(obraId: string, obraEscopoMacroId: string) {
  return useAsyncQuery<ObrasDestinoTransferencia | null>({
    fetcher: (signal) => getObrasDestinoTransferencia(obraId, obraEscopoMacroId, signal),
    queryKey: `transferencia-escopo-macro/${obraId}/${obraEscopoMacroId}`,
    initialData: null,
    forbiddenMessage: TRANSFERENCIA_FORBIDDEN_MESSAGE,
    errorMessage: TRANSFERENCIA_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
}

export const ESCOPO_FORBIDDEN_MESSAGE = "Você não tem permissão para adicionar escopo macro.";
export const ORIGENS_ESCOPO_ERROR_MESSAGE =
  "Não foi possível carregar as origens do escopo macro. Tente novamente.";
export const ESCOPOS_PADRAO_ERROR_MESSAGE =
  "Não foi possível carregar os escopos padrão do Tipo de Obra. Tente novamente.";
export const EQUIPAMENTOS_ERROR_MESSAGE =
  "Não foi possível carregar os equipamentos da embarcação. Tente novamente.";
export const NOME_COMPOSTO_ERROR_MESSAGE =
  "Não foi possível montar o nome do escopo. Tente novamente.";

export function useOrigensEscopoMacroDados(enabled = true) {
  return useAsyncQuery<OrigemEscopoMacro[]>({
    fetcher: (signal) => getOrigensEscopoMacro(signal),
    queryKey: "escopo-macro/origens",
    initialData: [],
    enabled,
    forbiddenMessage: ESCOPO_FORBIDDEN_MESSAGE,
    errorMessage: ORIGENS_ESCOPO_ERROR_MESSAGE,
  });
}

export function useEscoposPadraoDoTipoDados(tipoObraId: string | null) {
  return useAsyncQuery<EscopoPadraoDoTipo[]>({
    fetcher: (signal) => getEscoposPadraoDoTipo(tipoObraId ?? "", signal),
    queryKey: `escopo-macro/padrao/${tipoObraId ?? ""}`,
    initialData: [],
    enabled: tipoObraId !== null,
    forbiddenMessage: ESCOPO_FORBIDDEN_MESSAGE,
    errorMessage: ESCOPOS_PADRAO_ERROR_MESSAGE,
  });
}

export function useEquipamentosSfiDados(obraId: string, grupo: string | null, enabled = true) {
  return useAsyncQuery<EquipamentosSfi | null>({
    fetcher: (signal) => getEquipamentosSfi(obraId, grupo ?? undefined, signal),
    queryKey: `escopo-macro/equipamentos/${obraId}/${grupo ?? ""}`,
    initialData: null,
    enabled,
    forbiddenMessage: ESCOPO_FORBIDDEN_MESSAGE,
    errorMessage: EQUIPAMENTOS_ERROR_MESSAGE,
  });
}

export function useNomeComposto103Dados(obraId: string, parametros: ParametrosNomeComposto | null) {
  return useAsyncQuery<NomeComposto103 | null>({
    fetcher: (signal) =>
      parametros ? getNomeComposto103(obraId, parametros, signal) : Promise.resolve(null),
    queryKey: `escopo-macro/nome-composto/${obraId}/${parametros?.equipamentoSfiId ?? ""}/${parametros?.tipoManutencao ?? ""}`,
    initialData: null,
    enabled: parametros !== null,
    forbiddenMessage: ESCOPO_FORBIDDEN_MESSAGE,
    errorMessage: NOME_COMPOSTO_ERROR_MESSAGE,
  });
}

export const TRANSFERENCIA_TIPO_ERROR_MESSAGE =
  "Não foi possível carregar as Obras de destino. Tente novamente.";
export const TRANSFERENCIA_TIPO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para transferir Tipo de Obra.";
export const REVISAO_TRANSFERENCIA_TIPO_ERROR_MESSAGE =
  "Não foi possível carregar a revisão da transferência. Tente novamente.";

export function useObrasDestinoTransferenciaTipoDados(obraId: string, obraTipoObraId: string) {
  return useAsyncQuery<ObrasDestinoTipo | null>({
    fetcher: (signal) => getObrasDestinoTransferenciaTipo(obraId, obraTipoObraId, { signal }),
    queryKey: `transferencia-tipo-obra/${obraId}/${obraTipoObraId}`,
    initialData: null,
    forbiddenMessage: TRANSFERENCIA_TIPO_FORBIDDEN_MESSAGE,
    errorMessage: TRANSFERENCIA_TIPO_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
}

export function useRevisaoTransferenciaTipoDados(
  obraId: string,
  obraTipoObraId: string,
  parametros: ParametrosRevisaoTransferenciaTipo | null,
) {
  return useAsyncQuery<RevisaoTransferenciaTipo | null>({
    fetcher: (signal) =>
      parametros
        ? getRevisaoTransferenciaTipo(obraId, obraTipoObraId, parametros, { signal })
        : Promise.resolve(null),
    queryKey: `revisao-transferencia-tipo/${obraId}/${obraTipoObraId}/${parametros?.obraDestinoId ?? ""}/${parametros?.centroCustoDestinoId ?? ""}`,
    initialData: null,
    enabled: parametros !== null,
    forbiddenMessage: TRANSFERENCIA_TIPO_FORBIDDEN_MESSAGE,
    errorMessage: REVISAO_TRANSFERENCIA_TIPO_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
}
